import { useEffect, useState } from 'react';
import Modal from './Modal';
import { analysisApi, formatCurrency } from '../utils/api';
import { toast } from './Toast';
import { formatDateShort } from '../utils/dateFormat';
import { track, EVENTS } from '../utils/analytics';
import Icon from './Icon';

// Format a SQLite-style 'YYYY-MM-DD HH:MM:SS' (UTC) into Hebrew DD/MM/YYYY HH:MM
// in the user's local time. Returns null if the input is null/empty.
function fmtLastViewed(ts) {
  if (!ts) return null;
  const iso = ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return ts;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Admin-only price-change report. Driven by a per-restaurant "last viewed"
// timestamp stored on the server. Each fetch returns only changes from docs
// entered AFTER that timestamp; the user can advance the timestamp by clicking
// "סמן כנצפה". First-time viewers (no timestamp set) see the full history.
export default function PriceChangeReportModal({ onClose }) {
  const [loading, setLoading]       = useState(true);
  const [marking, setMarking]       = useState(false);
  const [rows,    setRows]          = useState([]);
  const [lastViewed, setLastViewed] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await analysisApi.priceChanges();
      setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
      setLastViewed(r.data?.last_viewed || null);
    } catch {
      toast('שגיאה בטעינת דוח שינויי מחיר', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleMarkViewed = async () => {
    setMarking(true);
    try {
      await analysisApi.markViewedPriceChanges();
      track(EVENTS.PRICE_CHANGES_REVIEWED, { change_count: rows.length });
      toast('הדוח סומן כנצפה');
      await load();   // re-fetch with the new cutoff in place (typically 0 rows)
    } catch (e) {
      toast(e.response?.data?.error || 'שגיאה בעדכון תאריך הדוח', 'error');
    } finally {
      setMarking(false);
    }
  };

  const ups   = rows.filter(r => r.diff > 0).sort((a, b) => b.diff - a.diff);
  const downs = rows.filter(r => r.diff < 0).sort((a, b) => a.diff - b.diff);

  const fmtPct  = (n) => `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
  const fmtDiff = (n) => `${n > 0 ? '+' : ''}${formatCurrency(n)}`;

  const lastViewedLabel = lastViewed
    ? `תאריך דוח אחרון: ${fmtLastViewed(lastViewed)}`
    : 'תצוגה ראשונית — מציג את כל ההיסטוריה';

  return (
    <Modal
      onClose={onClose}
      ariaLabel="דוח שינויי מחיר"
      size={1180}
      style={{ width: '92vw', maxHeight: '92vh', overflowY: 'auto' }}
    >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
      <h2 className="modal-title" style={{ margin: 0 }}>דוח שינויי מחיר</h2>

      <span style={{ color: 'var(--ink-muted)', fontSize: '0.88rem', flex: 1, minWidth: 200 }}>{lastViewedLabel}</span>

      <button
        className="btn btn-primary btn-sm"
        onClick={handleMarkViewed}
        disabled={loading || marking}
        title="עדכן את תאריך הדוח להיום, כך שהדוח הבא יציג רק מסמכים חדשים שנכנסו מעכשיו"
      >
        {marking ? 'מעדכן…' : <><Icon name="check" size={15} /> סמן כנצפה</>}
      </button>

      <button
        className="btn btn-outline btn-sm"
        onClick={async () => {
          try { await analysisApi.exportPriceChanges(); }
          catch (e) { toast(e.response?.data?.error || 'שגיאה בייצוא לאקסל', 'error'); }
        }}
        disabled={loading || rows.length === 0}
      >
        <Icon name="spreadsheet" size={15} /> ייצוא לאקסל
      </button>
    </div>

    {loading ? (
      <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
    ) : rows.length === 0 ? (
      <div className="empty-state">
        
        <p>{lastViewed
          ? 'לא נמצאו שינויי מחיר במסמכים שנכנסו מאז התאריך האחרון'
          : 'לא נמצאו שינויי מחיר'}</p>
      </div>
    ) : (
      <>
        <Section title="עליות"  icon="arrow-up"   color="#b91c1c" rows={ups}   fmtPct={fmtPct} fmtDiff={fmtDiff} />
        <Section title="ירידות" icon="arrow-down" color="#15803d" rows={downs} fmtPct={fmtPct} fmtDiff={fmtDiff} />
      </>
    )}

    <div className="modal-actions" style={{ marginTop: 16 }}>
      <button className="btn btn-secondary" onClick={onClose}>סגור</button>
    </div>
    </Modal>
  );
}

function Section({ title, icon, color, rows, fmtPct, fmtDiff }) {
  if (rows.length === 0) {
    return (
      <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f7f9fc', borderRadius: 8, color: 'var(--ink-muted)', fontSize: '0.9rem' }}>
        <span style={{ color, fontWeight: 700, marginLeft: 6 }}><Icon name={icon} size={13} /></span>
        {title}: אין שינויים
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 22 }}>
      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color, marginBottom: 8 }}>
        <Icon name={icon} size={14} /> {title} ({rows.length})
      </h3>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ספק</th>
              <th>שם מוצר</th>
              <th>מק"ט</th>
              <th>מחיר קודם</th>
              <th>תאריך חשבונית קודמת</th>
              <th>מחיר חדש</th>
              <th>תאריך חשבונית חדשה</th>
              <th>הפרש ₪</th>
              <th>הפרש %</th>
              <th>הפרש × כמות</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{r.supplier_name}</td>
                <td>{r.description}</td>
                <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{r.sku || '—'}</td>
                <td>{formatCurrency(r.prev_price)}</td>
                <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{formatDateShort(r.prev_date) || '—'}</td>
                <td style={{ fontWeight: 600 }}>{formatCurrency(r.current_price)}</td>
                <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{formatDateShort(r.current_date) || '—'}</td>
                <td style={{ color, fontWeight: 700 }}>{fmtDiff(r.diff)}</td>
                <td style={{ color, fontWeight: 700 }}>{fmtPct(r.diff_pct)}</td>
                <td style={{ color, fontWeight: 700 }}>{fmtDiff(r.diff_x_qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
